import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { trackingDashboardPath, userItineraryNavigationPaths } from '../../utils/userLanding';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  LayoutDashboard,
  Users,
  Shield,
  FileText,
  ClipboardList,
  BarChart3,
  Bot,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Database,
  CalendarDays,
  Tags,
  Activity,
} from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout, canAccessAssistant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [openAdminGroups, setOpenAdminGroups] = useState({
    Usuarios: true,
    Formularios: true,
    Herramientas: true,
  });

  const isAdmin = user?.role_level === 1;

  const adminDashboardItem = { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true };
  const trackingDashboardItem = { path: trackingDashboardPath(user), icon: Activity, label: 'Dashboard Seguimiento', exact: true };
  const adminNavGroups = [
    {
      label: 'Usuarios',
      items: [
        { path: '/admin/users', icon: Users, label: 'Usuarios' },
        { path: '/admin/roles', icon: Shield, label: 'Roles' },
      ],
    },
    {
      label: 'Formularios',
      items: [
        { path: '/admin/forms', icon: FileText, label: 'Formularios' },
        { path: '/admin/databases', icon: Database, label: 'Bases de Datos' },
        { path: '/admin/form-segments', icon: Tags, label: 'Secciones' },
        { path: '/admin/responses', icon: ClipboardList, label: 'Ver Respuestas' },
      ],
    },
    {
      label: 'Herramientas',
      items: [
        { path: '/admin/itineraries', icon: CalendarDays, label: 'Itinerarios' },
        { path: '/admin/visit-plans', icon: ClipboardList, label: 'Plantillas de visita' },
        { path: '/admin/assistant', icon: Bot, label: 'Asistente de Neveras' },
      ],
    },
  ];
  const adminNavItems = [adminDashboardItem, ...(user?.can_access_tracking_dashboard ? [trackingDashboardItem] : []), ...adminNavGroups.flatMap((group) => group.items)];

  const itineraryNavItems = userItineraryNavigationPaths(user).map((path) => (
    path === '/dashboard'
      ? { path, icon: LayoutDashboard, label: 'Agenda del día', exact: true, testId: 'nav-dashboard' }
      : { path, icon: CalendarDays, label: 'Agenda semanal', testId: 'nav-agenda-recurrente' }
  ));

  const userNavItems = [
    ...itineraryNavItems,
    ...(user?.can_access_tracking_dashboard ? [trackingDashboardItem] : []),
    { path: '/dashboard/forms', icon: FileText, label: 'Formularios' },
    { path: '/dashboard/my-responses', icon: ClipboardList, label: 'Mis Respuestas' },
    ...(canAccessAssistant()
      ? [{ path: '/dashboard/assistant', icon: Bot, label: 'Asistente de Neveras' }]
      : []),
    ...(user?.allowed_response_form_ids?.length > 0
      ? [{ path: '/dashboard/responses', icon: BarChart3, label: 'Ver Respuestas' }]
      : []),
  ];

  const navItems = isAdmin ? adminNavItems : userNavItems;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ item }) => {
    // Fix: Proper active detection
    let isActive = false;
    if (item.exact) {
      isActive = location.pathname === item.path;
    } else {
      // Must match exactly or be a sub-path (with /)
      isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    }

    return (
      <Link
        to={item.path}
        onClick={() => setMobileSidebarOpen(false)}
        className={`sidebar-item ${isActive ? 'active' : ''}`}
        data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        aria-label={item.label}
      >
        <item.icon className="w-5 h-5" />
        {(sidebarOpen || mobileSidebarOpen) && <span>{item.label}</span>}
      </Link>
    );
  };

  const AdminNavGroup = ({ group }) => {
    const isOpen = openAdminGroups[group.label];
    const isExpandedSidebar = sidebarOpen || mobileSidebarOpen;

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => setOpenAdminGroups((groups) => ({ ...groups, [group.label]: open }))}
        className="sidebar-nav-group"
      >
        {isExpandedSidebar && (
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="sidebar-nav-group-title h-6 w-full justify-between rounded-md py-0"
              aria-label={`${isOpen ? 'Contraer' : 'Expandir'} grupo ${group.label}`}
              aria-expanded={isOpen}
            >
              <span>{group.label}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
            </Button>
          </CollapsibleTrigger>
        )}
        <CollapsibleContent className="space-y-1">
          {group.items.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header border-b px-4 h-16 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          data-testid="mobile-menu-toggle"
          aria-label={mobileSidebarOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
        >
          {mobileSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
        <h1 className="font-bold text-lg">StratForm</h1>
        <UserMenu user={user} onLogout={handleLogout} />
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen bg-card border-r transition-all duration-300
          w-64 ${sidebarOpen ? 'lg:w-64' : 'lg:w-16'}
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b">
            {sidebarOpen && (
              <h1 className="font-bold text-xl text-foreground">StratForm</h1>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex"
              data-testid="sidebar-toggle"
              aria-label={sidebarOpen ? 'Contraer barra lateral' : 'Expandir barra lateral'}
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1" aria-label="Navegación principal">
               {isAdmin ? (
                <>
                  <NavItem item={adminDashboardItem} />
                  {user?.can_access_tracking_dashboard && <NavItem item={trackingDashboardItem} />}
                  {adminNavGroups.map((group) => (
                    <AdminNavGroup key={group.label} group={group} />
                  ))}
                </>
              ) : navItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </nav>
          </ScrollArea>

          {/* User section - Desktop */}
          <div className="hidden lg:block p-4 border-t">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-indigo-600 text-white">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.role_name}</p>
                </div>
              )}
              {sidebarOpen && (
                <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        transition-all duration-300 pt-16 lg:pt-0
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}
      `}>
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 items-center justify-between px-6 border-b bg-card">
          <div>
            <h2 className="text-lg font-semibold">
              {navItems.find(item =>
                item.exact
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path)
               )?.label || (isAdmin ? 'Dashboard' : itineraryNavItems.length ? 'Agenda del día' : 'Formularios')}
            </h2>
          </div>
          <UserMenu user={user} onLogout={handleLogout} />
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const UserMenu = ({ user, onLogout }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu-trigger">
        <Avatar className="h-9 w-9">
          <AvatarFallback className="bg-indigo-600 text-white">
            {user?.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel>
        <div className="flex flex-col">
          <span>{user?.name}</span>
          <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-muted-foreground">
        <Shield className="mr-2 h-4 w-4" />
        {user?.role_name}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onLogout} className="text-destructive" data-testid="dropdown-logout">
        <LogOut className="mr-2 h-4 w-4" />
        Cerrar sesión
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export default DashboardLayout;
